/**
 * Inline editor for a habit's check-in time windows.
 *
 * Rendered inside the Habit edit dialog when check-in is enabled. Self-manages
 * its own fetch/create/delete against /api/check-ins so the parent form stays
 * focused on the habit's own fields. Windows are minutes-since-local-midnight;
 * a window may span midnight. With no window a habit is eligible all day.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  IconButton,
  Typography,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface CheckInWindow {
  id: number;
  label: string | null;
  start_minute: number;
  end_minute: number;
  active_days: string | null;
}

const minutesToTime = (minutes: number): string => {
  const h = String(Math.floor(minutes / 60)).padStart(2, '0');
  const m = String(minutes % 60).padStart(2, '0');
  return `${h}:${m}`;
};

const timeToMinutes = (value: string): number => {
  const [h, m] = value.split(':').map((n) => parseInt(n, 10));
  return h * 60 + m;
};

const describeDays = (activeDays: string | null): string => {
  if (!activeDays) return 'Every day';
  return activeDays
    .split(',')
    .filter((p) => p.trim() !== '')
    .map((p) => DAY_LABELS[Number(p)])
    .join(', ');
};

interface Props {
  habitId: number;
}

const CheckInWindowsEditor: React.FC<Props> = ({ habitId }) => {
  const [windows, setWindows] = useState<CheckInWindow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [start, setStart] = useState('09:00');
  const [end, setEnd] = useState('17:00');
  const [label, setLabel] = useState('');
  const [days, setDays] = useState<number[]>([]);

  const fetchWindows = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/check-ins/habits/${habitId}/windows`);
      setWindows(res.data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load windows'));
    }
  }, [habitId]);

  useEffect(() => {
    fetchWindows();
  }, [fetchWindows]);

  const addWindow = async () => {
    const startMinute = timeToMinutes(start);
    const endMinute = timeToMinutes(end);
    if (startMinute === endMinute) {
      setError('Start and end times must differ');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/api/check-ins/habits/${habitId}/windows`, {
        label: label.trim() || null,
        start_minute: startMinute,
        end_minute: endMinute,
        active_days: days.length > 0 ? [...days].sort((a, b) => a - b).join(',') : null,
      });
      setLabel('');
      setDays([]);
      setError(null);
      await fetchWindows();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to add window'));
    } finally {
      setSaving(false);
    }
  };

  const deleteWindow = async (id: number) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/check-ins/windows/${id}`);
      await fetchWindows();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete window'));
    }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        Time windows (optional — no window means eligible all day)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ my: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={0.5} sx={{ my: 1 }}>
        {windows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No windows — eligible all day.
          </Typography>
        ) : (
          windows.map((w) => (
            <Stack key={w.id} direction="row" alignItems="center" spacing={1}>
              <Chip
                size="small"
                label={`${w.label ? `${w.label}: ` : ''}${minutesToTime(w.start_minute)}–${minutesToTime(
                  w.end_minute,
                )} · ${describeDays(w.active_days)}`}
              />
              <IconButton
                size="small"
                aria-label={`Delete window ${w.id}`}
                onClick={() => deleteWindow(w.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))
        )}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          type="time"
          label="Start"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 120 }}
        />
        <TextField
          size="small"
          type="time"
          label="End"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 120 }}
        />
        <TextField
          size="small"
          label="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={{ width: 130 }}
        />
      </Stack>

      <ToggleButtonGroup
        size="small"
        value={days}
        onChange={(_e, next) => setDays(next as number[])}
        aria-label="active days"
        sx={{ mt: 1, flexWrap: 'wrap' }}
      >
        {DAY_LABELS.map((d, idx) => (
          <ToggleButton key={d} value={idx} aria-label={d}>
            {d}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ mt: 1 }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addWindow}
          disabled={saving}
          variant="outlined"
        >
          Add window
        </Button>
      </Box>
    </Box>
  );
};

export default CheckInWindowsEditor;
